/**
 * Web Serial transport (Chrome and Edge 89+, Firefox 151+; secure contexts only: localhost or HTTPS).
 * The chosen port is remembered by USB vendor/product id so the app can reopen it without the picker,
 * including after the balance is unplugged and plugged back in.
 */
import { createLineSplitter } from "./lines";
import type { BalanceTransport, ProtocolId, SerialSettings, TransportCallbacks } from "./types";

interface SerialPortInfo {
  usbVendorId?: number;
  usbProductId?: number;
}

export interface SerialPortLike {
  open: (options: SerialSettings & { bufferSize?: number; flowControl?: "none" | "hardware" }) => Promise<void>;
  close: () => Promise<void>;
  readonly readable: ReadableStream<Uint8Array> | null;
  readonly writable: WritableStream<Uint8Array> | null;
  getInfo: () => SerialPortInfo;
}

interface SerialLike extends EventTarget {
  requestPort: (options?: { filters?: SerialPortInfo[] }) => Promise<SerialPortLike>;
  getPorts: () => Promise<SerialPortLike[]>;
}

// Persisted in browsers: a new key would forget every remembered balance port.
const STORAGE_KEY = "perfume.balance.serial";
const SESSION_KEY = "perfume.balance.serial.active";

export interface RememberedPort extends SerialPortInfo {
  protocol: ProtocolId;
}

export interface SerialSupport {
  supported: boolean;
  /** Why serial is unavailable, in words the user can act on. */
  reason?: string;
}

function serialApi(): SerialLike | null {
  if (typeof navigator === "undefined") return null;
  return (navigator as Navigator & { serial?: SerialLike }).serial ?? null;
}

export function serialSupport(): SerialSupport {
  if (typeof window !== "undefined" && !window.isSecureContext) {
    return {
      supported: false,
      reason: "USB balances need a secure page: open the app on localhost or over HTTPS.",
    };
  }
  if (!serialApi()) {
    return {
      supported: false,
      reason:
        "This browser can't talk to USB balances. Use Chrome or Edge (or Firefox 151+), or use keyboard mode or manual entry.",
    };
  }
  return { supported: true };
}

export function rememberedPort(): RememberedPort | null {
  try {
    return parseRemembered(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function parseRemembered(raw: string | null): RememberedPort | null {
  if (!raw) return null;
  const value: unknown = JSON.parse(raw);
  if (!value || typeof value !== "object") return null;
  const saved = value as Partial<RememberedPort>;
  if (!["and", "mt-sics", "generic"].includes(saved.protocol ?? "")) return null;
  for (const id of [saved.usbVendorId, saved.usbProductId]) {
    if (id !== undefined && (!Number.isInteger(id) || id < 0 || id > 65535)) return null;
  }
  return { protocol: saved.protocol!, usbVendorId: saved.usbVendorId, usbProductId: saved.usbProductId };
}

/** Only this tab's active connection is restored. A new tab must not claim another tab's balance. */
export function rememberedSessionPort(): RememberedPort | null {
  try {
    return parseRemembered(window.sessionStorage.getItem(SESSION_KEY));
  } catch {
    return null;
  }
}

export function rememberSessionPort(port: SerialPortLike, protocol: ProtocolId): void {
  try {
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ...port.getInfo(), protocol }));
  } catch {
    // The balance still works when browser storage is unavailable; only automatic restore is lost.
  }
}

export function forgetSessionPort(): void {
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Storage may be blocked.
  }
}

export function rememberPort(port: SerialPortLike, protocol: ProtocolId): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...port.getInfo(), protocol }));
  } catch {
    // Storage can be unavailable (private mode); the picker simply shows again next time.
  }
}

function matches(port: SerialPortLike, remembered: RememberedPort): boolean {
  const info = port.getInfo();
  return info.usbVendorId === remembered.usbVendorId && info.usbProductId === remembered.usbProductId;
}

/** Opens the browser's port picker (needs a user gesture: call it straight from a click handler). */
export async function pickSerialPort(): Promise<SerialPortLike> {
  const serial = serialApi();
  if (!serial) throw new Error(serialSupport().reason ?? "Web Serial is not available");
  return serial.requestPort();
}

/** The remembered port if the browser already has permission for it (no picker). */
export async function findRememberedPort(remembered = rememberedPort()): Promise<SerialPortLike | null> {
  const serial = serialApi();
  if (!serial || !remembered) return null;
  const ports = await serial.getPorts();
  const matching = ports.filter((port) => matches(port, remembered));
  if (matching.length > 1)
    throw new Error(
      "More than one authorized USB device matches this balance. Choose the balance again from the USB menu.",
    );
  return matching[0] ?? null;
}

/** Calls `onConnect` when the remembered balance is plugged back in; returns an unsubscribe function. */
export function watchSerialReconnect(onConnect: () => void, remembered = rememberedPort()): () => void {
  const serial = serialApi();
  if (!serial) return () => undefined;
  const listener = (event: Event) => {
    const port = (event.target ?? null) as SerialPortLike | null;
    if (remembered && port && typeof port.getInfo === "function" && matches(port, remembered)) onConnect();
  };
  serial.addEventListener("connect", listener);
  return () => serial.removeEventListener("connect", listener);
}

export function createSerialTransport(port: SerialPortLike, settings: SerialSettings): BalanceTransport {
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  let keepReading = false;
  let closedOnPurpose = false;
  let reading: Promise<void> | null = null;
  let writes: Promise<void> = Promise.resolve();
  let acceptingWrites = false;

  async function readLoop(callbacks: TransportCallbacks) {
    const decoder = new TextDecoder();
    const splitter = createLineSplitter(callbacks.onLine);
    let failure: Error | undefined;
    while (keepReading && port.readable) {
      reader = port.readable.getReader();
      try {
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          if (value?.length) {
            callbacks.onActivity?.();
            splitter.push(decoder.decode(value, { stream: true }));
          }
        }
      } catch (error) {
        // Framing and parity errors are recoverable (the loop takes a new reader); a lost device is not.
        failure = error instanceof Error || error instanceof DOMException ? error : new Error(String(error));
        if (failure.name === "NetworkError") keepReading = false;
        else {
          // The line in progress lost a byte: joined to what follows it could read as a different mass.
          splitter.discard();
          callbacks.onActivity?.();
        }
      } finally {
        reader.releaseLock();
        reader = null;
      }
      if (!port.readable) break;
    }
    acceptingWrites = false;
    await writes;
    try {
      await port.close();
    } catch {
      // Already closed (unplugged).
    }
    callbacks.onClose(closedOnPurpose ? undefined : (failure ?? new Error("The balance was disconnected")));
  }

  return {
    kind: "serial",
    label: "USB / serial",
    canWrite: true,
    async open(callbacks) {
      await port.open({ ...settings, bufferSize: 1024, flowControl: "none" });
      keepReading = true;
      closedOnPurpose = false;
      acceptingWrites = true;
      reading = readLoop(callbacks);
    },
    write(text) {
      const write = writes.then(async () => {
        if (!acceptingWrites || !port.writable) throw new Error("The balance is not connected");
        const writer = port.writable.getWriter();
        try {
          await writer.write(new TextEncoder().encode(text));
        } finally {
          writer.releaseLock();
        }
      });
      writes = write.catch(() => undefined);
      return write;
    },
    async close() {
      closedOnPurpose = true;
      keepReading = false;
      acceptingWrites = false;
      await reader?.cancel().catch(() => undefined);
      // Reader cancellation schedules the loop's finally block; wait until it has actually released
      // the lock and closed the port before the next connection tries to open the same device.
      await reading;
    },
  };
}
